// Beat - canonical enums (source of truth for both web/ and functions/)
// Locked seed lists per the plan. Designations + channels are admin-editable
// at runtime (stored in config/global) but these are the Phase 1 seeds.

export const ROLES = [
  'owner',
  'manager',
  'agent',
  'crew',
  'freelancer',
  'client',
] as const;
export type Role = typeof ROLES[number];

// 18 crew designations: 10 core + 8 extended.
export const DESIGNATIONS_CORE = [
  'strategist',
  'content_creator',
  'art_director',
  'videographer',
  'video_editor',
  'graphic_designer',
  'photographer',
  'copywriter',
  'smm',
  'account_exec',
] as const;

export const DESIGNATIONS_EXTENDED = [
  'creative_director',
  'producer',
  'motion_designer',
  'retoucher',
  'community_manager',
  'talent_coordinator',
  'arabic_copywriter',
  'media_buyer',
] as const;

export const DESIGNATIONS = [
  ...DESIGNATIONS_CORE,
  ...DESIGNATIONS_EXTENDED,
] as const;
export type Designation = typeof DESIGNATIONS[number];

// Workflow stages for a creative: the API's seven (api/app/steps.py STAGE_LABEL).
// 'strategy' and 'content' were the Firebase-era stages and never reached the API (FE-19).
export const STAGES = [
  'brief',
  'shoot',
  'raws_review',
  'edit',
  'dual_review',
  'done',
  'posted',
] as const;
export type Stage = typeof STAGES[number];

// Strategy lifecycle.
export const STRATEGY_STATUS = ['draft', 'active', 'done', 'archived'] as const;
export type StrategyStatus = typeof STRATEGY_STATUS[number];

// Creative lifecycle.
export const CREATIVE_STATUS = [
  'planned',
  'in_progress',
  'blocked',
  'done',
] as const;
export type CreativeStatus = typeof CREATIVE_STATUS[number];

// Library card "state" - denormalized summary used by the IG-feed view.
export const CREATIVE_STATES = ['Draft', 'Upload', 'Approved', 'Posted'] as const;
export type CreativeState = typeof CREATIVE_STATES[number];

// Creative formats. Instagram sub-formats live here, NOT on channels.
export const CREATIVE_TYPES = [
  'video',
  'reel',
  'static',
  'carousel',
  'story',
  'photo',
  'copy',
] as const;
export type CreativeType = typeof CREATIVE_TYPES[number];

// 12 universal categories (multi-select).
// "Media Buying" is NOT here - it became the isPaid boolean.
export const CATEGORIES = [
  'Awareness',
  'Authority & Trust',
  'Educational',
  'Promotional / Offers',
  'Launch / Announcement',
  'Testimonial / Social Proof',
  'Engagement / Community',
  'Seasonal / Event',
  'Lead Generation',
  'UGC / Influencer',
  'CRM / Retention',
  'Retargeting',
] as const;
export type Category = typeof CATEGORIES[number];

// Funnel tag (single-select, no workflow effect).
export const FUNNEL_STAGES = ['TOF', 'MOF', 'BOF'] as const;
export type FunnelStage = typeof FUNNEL_STAGES[number];

// Paid platforms - used only when creative.isPaid is true.
export const AD_PLATFORMS = [
  'meta_ads',
  'google_ads',
  'tiktok_ads',
  'snapchat_ads',
  'youtube_ads',
  'linkedin_ads',
] as const;
export type AdPlatform = typeof AD_PLATFORMS[number];

// 13 organic/owned channels (default seed).
export const CHANNELS_DEFAULT = [
  'instagram',
  'tiktok',
  'snapchat',
  'youtube',
  'youtube_shorts',
  'facebook',
  'linkedin',
  'x_twitter',
  'threads',
  'pinterest',
  'whatsapp_channel',
  'telegram',
  'google_business',
] as const;

// 5 optional channels (admin-toggleable per client).
export const CHANNELS_OPTIONAL = [
  'whatsapp_catalog',
  'website',
  'email',
  'print',
  'ooh',
] as const;

export const CHANNELS = [
  ...CHANNELS_DEFAULT,
  ...CHANNELS_OPTIONAL,
] as const;
export type Channel = typeof CHANNELS[number];

// Verticals + their default taxonomy templates.
// Generic schema fields on creatives: taxonomyPrimary, taxonomySecondary, talentId.
// UI labels come from the client's taxonomy config (which clones one of these).
export const VERTICALS = [
  'clinic',
  'fashion',
  'fnb',
  'retail',
  'real_estate',
  'services',
  'hospitality',
  'automotive',
  'education',
  'fitness',
  'beauty',
  'jewelry',
  'other',
] as const;
export type Vertical = typeof VERTICALS[number];

export interface TaxonomyTemplate {
  vertical: Vertical;
  label: string;          // human label for the template card
  primary: { label: string; sampleValues: string[] };
  secondary: { label: string; sampleValues: string[] };
  talent: { label: string; enabled: boolean };
}

export const TAXONOMY_TEMPLATES: Record<Vertical, TaxonomyTemplate> = {
  clinic: {
    vertical: 'clinic',
    label: 'Clinic (Medical / Aesthetic)',
    primary:   { label: 'Specialty',   sampleValues: ['Dental', 'Dermatology', 'Plastic Surgery', 'ENT', 'Gynecology', 'Bariatric', 'Internal Medicine', 'Physiotherapy'] },
    secondary: { label: 'Treatment',   sampleValues: ['veneer', 'whitening', 'botox', 'hydrafacial', 'rhinoplasty', 'gastric balloon'] },
    talent:    { label: 'Doctor',      enabled: true },
  },
  fashion: {
    vertical: 'fashion',
    label: 'Fashion / Apparel',
    primary:   { label: 'Category', sampleValues: ['Trousers', 'Tops', 'Outerwear', 'Footwear', 'Accessories'] },
    secondary: { label: 'Style',    sampleValues: ['denim', 'chinos', 'linen', 'wool', 'silk'] },
    talent:    { label: 'Model',    enabled: true },
  },
  fnb: {
    vertical: 'fnb',
    label: 'F&B (Restaurant / Cafe)',
    primary:   { label: 'Cuisine', sampleValues: ['Italian', 'Japanese', 'Levantine', 'Indian', 'French'] },
    secondary: { label: 'Dish',    sampleValues: ['pasta', 'sushi', 'mezze', 'curry', 'pastry'] },
    talent:    { label: 'Chef',    enabled: true },
  },
  retail: {
    vertical: 'retail',
    label: 'Retail',
    primary:   { label: 'Department',         sampleValues: ['Electronics', 'Home', 'Beauty', 'Toys'] },
    secondary: { label: 'Sub-category',       sampleValues: ['audio', 'kitchen', 'skincare'] },
    talent:    { label: 'Brand Ambassador',   enabled: false },
  },
  real_estate: {
    vertical: 'real_estate',
    label: 'Real Estate',
    primary:   { label: 'Project',   sampleValues: ['Marina Heights', 'Downtown Square'] },
    secondary: { label: 'Unit Type', sampleValues: ['1BR', '2BR', 'Penthouse', 'Townhouse'] },
    talent:    { label: 'Agent',     enabled: true },
  },
  services: {
    vertical: 'services',
    label: 'Services (B2B)',
    primary:   { label: 'Service Line', sampleValues: ['Consulting', 'Training', 'Audit'] },
    secondary: { label: 'Sub-service',  sampleValues: ['strategy', 'workshops', 'compliance'] },
    talent:    { label: 'Expert',       enabled: true },
  },
  hospitality: {
    vertical: 'hospitality',
    label: 'Hospitality / Hotels',
    primary:   { label: 'Property',         sampleValues: ['Main Tower', 'Beach Wing'] },
    secondary: { label: 'Room / Experience', sampleValues: ['suite', 'spa', 'pool day'] },
    talent:    { label: 'Concierge or Chef', enabled: true },
  },
  automotive: {
    vertical: 'automotive',
    label: 'Automotive',
    primary:   { label: 'Make-Model',    sampleValues: ['Toyota Camry', 'BMW X5'] },
    secondary: { label: 'Trim',          sampleValues: ['base', 'sport', 'luxury'] },
    talent:    { label: 'Sales Advisor', enabled: true },
  },
  education: {
    vertical: 'education',
    label: 'Education',
    primary:   { label: 'Program',     sampleValues: ['MBA', 'Coding Bootcamp', 'IELTS Prep'] },
    secondary: { label: 'Level',       sampleValues: ['beginner', 'intermediate', 'advanced'] },
    talent:    { label: 'Instructor',  enabled: true },
  },
  fitness: {
    vertical: 'fitness',
    label: 'Fitness / Wellness',
    primary:   { label: 'Class Type', sampleValues: ['Yoga', 'CrossFit', 'Pilates', 'Boxing'] },
    secondary: { label: 'Program',    sampleValues: ['8-week shred', 'mobility', 'strength'] },
    talent:    { label: 'Coach',      enabled: true },
  },
  beauty: {
    vertical: 'beauty',
    label: 'Beauty (Salon / Spa)',
    primary:   { label: 'Service',   sampleValues: ['Hair', 'Nails', 'Massage', 'Facials'] },
    secondary: { label: 'Treatment', sampleValues: ['balayage', 'gel', 'deep tissue', 'glow'] },
    talent:    { label: 'Stylist',   enabled: true },
  },
  jewelry: {
    vertical: 'jewelry',
    label: 'Jewelry / Luxury',
    primary:   { label: 'Collection', sampleValues: ['Bridal', 'Daily', 'Heritage'] },
    secondary: { label: 'Piece',      sampleValues: ['ring', 'necklace', 'earrings'] },
    talent:    { label: 'Muse',       enabled: false },
  },
  other: {
    vertical: 'other',
    label: 'Generic (catchall)',
    primary:   { label: 'Tag',     sampleValues: [] },
    secondary: { label: 'Sub-tag', sampleValues: [] },
    talent:    { label: 'Talent',  enabled: false },
  },
};

// Routing and the art director gates live in the API (api/app/work_routing.py, approver_routing.py);
// the Cloud Functions tables that sat here were removed with the Firebase backend (FE-19).
